import type { Applicant } from "../internal-owned/school-admissions-v2-1/types";
import { isFinancialAidAccessEnabledForApplication } from "./financialAidAccess";

export const ADMISSIONS_DASHBOARD_ARTIFACT_ROUTE =
  "/school/admissions/dashboard";
export const ADMISSIONS_DASHBOARD_ARTIFACT_LIMIT = 200;

export const ADMISSIONS_DASHBOARD_STATUS_ORDER = [
  "Applied",
  "In-Progress",
  "Scheduled",
  "Accepted",
  "Enrolled",
  "Waitlisted",
  "Denied",
  "Archived",
] as const satisfies ReadonlyArray<Applicant["status"]>;

export type AdmissionsDashboardChartDimension =
  | "status"
  | "grade"
  | "assignee"
  | "submission_month";

export type AdmissionsDashboardChartKind =
  | "bar"
  | "stacked-bar"
  | "line"
  | "area";

export type AdmissionsDashboardApplicantProjection = Applicant & {
  applicationCode?: string | null;
  familyName?: string | null;
  submittedAtEpochSeconds?: number | null;
  submittedAtIso?: string | null;
  stageReferenceEpochSeconds?: number | null;
  interviewDateEpochSeconds?: number | null;
  interviewDateIso?: string | null;
  priorityCount?: 0 | 1;
  financialAidEnabled?: boolean;
};

export type AdmissionsDashboardSummaryRow = {
  key: string;
  label: string;
  count: number;
  priorityCount: number;
  standardCount: number;
};

export type AdmissionsDashboardArtifactDataset = {
  sourceId: "admissions_dashboard_live";
  route: string;
  generatedAt: string;
  windowLimit: number;
  totalCount: number;
  applicantCount: number;
  applicants: AdmissionsDashboardApplicantProjection[];
  summaries: {
    status: AdmissionsDashboardSummaryRow[];
    grade: AdmissionsDashboardSummaryRow[];
    assignee: AdmissionsDashboardSummaryRow[];
    submissionMonth: AdmissionsDashboardSummaryRow[];
  };
};

export type AdmissionsDashboardChartRequest = {
  kind: AdmissionsDashboardChartKind;
  dimension: AdmissionsDashboardChartDimension;
  title: string;
  subtitle: string;
  preset?: "status_page_data";
};

export type AdmissionsDashboardArtifactSupport = {
  supported: boolean;
  fallbackToChat: boolean;
  reason?: string;
  sourceId?: "admissions_dashboard_live";
  chartRequest?: AdmissionsDashboardChartRequest;
};

const readString = (value: unknown): string => String(value ?? "").trim();
const UUID_LIKE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UNSUPPORTED_DASHBOARD_CHART_PATTERN =
  /\b(scatter|bubble|radar|pie|donut|heatmap)\b/i;
const PAGE_SCOPED_CHART_PATTERN =
  /\b(data on this page|current page data|page data|this dashboard|current dashboard|dashboard data|this page|current page|this route|current route|this view|current view|this screen|current screen)\b/i;
const MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
});
const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});
const SHORT_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asUnixSeconds = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toIsoFromUnixSeconds = (epochSeconds: unknown): string | null => {
  const value = asUnixSeconds(epochSeconds);
  if (value == null || value <= 0) {
    return null;
  }
  return new Date(value * 1000).toISOString();
};

const formatShortDateFromUnixSeconds = (epochSeconds: unknown): string => {
  const value = asUnixSeconds(epochSeconds);
  if (value == null || value <= 0) {
    return "";
  }
  return SHORT_DATE_FORMATTER.format(new Date(value * 1000));
};

const formatShortDateTimeFromUnixSeconds = (epochSeconds: unknown): string => {
  const value = asUnixSeconds(epochSeconds);
  if (value == null || value <= 0) {
    return "";
  }
  return SHORT_DATE_TIME_FORMATTER.format(new Date(value * 1000));
};

const resolveStageReferenceEpochSeconds = (
  value: Record<string, unknown>,
): number | null => {
  for (const candidate of [
    value.updatedAt,
    value.submittedAt,
    value.createdAt,
  ]) {
    const parsed = asUnixSeconds(candidate);
    if (parsed != null && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

const normalizeArtifactRoutePathname = (
  value: string | null | undefined,
): string => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "/";
  }
  const pathname =
    raw.startsWith("http://") || raw.startsWith("https://")
      ? new URL(raw).pathname
      : raw;
  const withLeadingSlash = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const withoutQuery = withLeadingSlash.split("?")[0] ?? withLeadingSlash;
  const collapsed = withoutQuery.replace(/\/{2,}/g, "/").toLowerCase();
  return collapsed.length > 1 && collapsed.endsWith("/")
    ? collapsed.slice(0, -1)
    : collapsed;
};

export const isAdmissionsDashboardArtifactRoute = (
  pathname: string | null | undefined,
): boolean => {
  const normalized = normalizeArtifactRoutePathname(pathname);
  return (
    normalized === "/school/admissions" ||
    normalized === ADMISSIONS_DASHBOARD_ARTIFACT_ROUTE
  );
};

const resolveAssigneeLabel = (...candidates: unknown[]): string => {
  for (const candidate of candidates) {
    const normalized = readString(candidate);
    if (!normalized || UUID_LIKE_PATTERN.test(normalized)) {
      continue;
    }
    return normalized;
  }
  return "";
};

const toApplicantStatus = (value: unknown): Applicant["status"] => {
  const normalized = readString(value).toLowerCase();
  if (!normalized) return "Applied";
  if (normalized === "draft" || normalized === "submitted") return "Applied";
  if (normalized === "under_review" || normalized.includes("review"))
    return "In-Progress";
  if (normalized === "interview_scheduled") return "Scheduled";
  if (normalized === "accepted" || normalized === "accepted_pending_enrollment")
    return "Accepted";
  if (normalized === "enrolled") return "Enrolled";
  if (normalized === "waitlisted") return "Waitlisted";
  if (normalized === "denied") return "Denied";
  if (normalized === "withdrawn" || normalized === "acceptance_expired")
    return "Archived";
  return "Applied";
};

const normalizeGradeLabel = (value: unknown): string => {
  const raw = readString(value);
  if (!raw) return "Grade K";

  const cleaned = raw.replace(/[_-]+/g, " ").trim();
  const token = cleaned.replace(/^grade\s*/i, "").trim();
  if (!token) return "Grade K";

  if (/^k(?:indergarten)?$/i.test(token)) {
    return "Grade K";
  }

  if (/^\d+$/.test(token)) {
    return `Grade ${String(Number(token))}`;
  }

  if (/^[a-z]$/i.test(token)) {
    return `Grade ${token.toUpperCase()}`;
  }

  return `Grade ${token.replace(/\b\w/g, (char) => char.toUpperCase())}`;
};

const sortGradeLabel = (left: string, right: string): number => {
  const normalize = (value: string): { order: number; display: string } => {
    const token = value.replace(/^Grade\s+/i, "").trim();
    if (/^K$/i.test(token)) return { order: -1, display: value };
    const numeric = Number(token);
    if (Number.isFinite(numeric)) return { order: numeric, display: value };
    return { order: Number.MAX_SAFE_INTEGER, display: value };
  };
  const a = normalize(left);
  const b = normalize(right);
  if (a.order !== b.order) {
    return a.order - b.order;
  }
  return a.display.localeCompare(b.display);
};

const toMonthBucket = (epochSeconds: number | null | undefined): string => {
  if (!epochSeconds || epochSeconds <= 0) {
    return "Unknown month";
  }
  return MONTH_FORMATTER.format(new Date(epochSeconds * 1000));
};

const buildSummaryRows = (
  applicants: AdmissionsDashboardApplicantProjection[],
  keyResolver: (applicant: AdmissionsDashboardApplicantProjection) => string,
  sortRows: (
    left: AdmissionsDashboardSummaryRow,
    right: AdmissionsDashboardSummaryRow,
  ) => number,
): AdmissionsDashboardSummaryRow[] => {
  const rowsByKey = new Map<string, AdmissionsDashboardSummaryRow>();
  for (const applicant of applicants) {
    const label = keyResolver(applicant) || "Unassigned";
    const key = label.toLowerCase();
    const current = rowsByKey.get(key) ?? {
      key,
      label,
      count: 0,
      priorityCount: 0,
      standardCount: 0,
    };
    current.count += 1;
    if (applicant.priority) {
      current.priorityCount += 1;
    } else {
      current.standardCount += 1;
    }
    rowsByKey.set(key, current);
  }
  return [...rowsByKey.values()].sort(sortRows);
};

export const toAdmissionsDashboardApplicant = (
  value: Record<string, unknown>,
): AdmissionsDashboardApplicantProjection => {
  const applicant = asRecord(value.applicant);
  const primaryContact = asRecord(applicant.primaryContact);
  const contactFirstName = readString(primaryContact.firstName);
  const contactLastName = readString(primaryContact.lastName);
  const contactName = [contactFirstName, contactLastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const children = Array.isArray(value.children) ? value.children : [];
  const childRecords = children.map((child) => asRecord(child));
  const firstChild = asRecord(children[0]);
  const childInfo = asRecord(firstChild.childInfo);
  const childNames = childRecords
    .map((child) => {
      const info = asRecord(child.childInfo);
      return [readString(info.firstName), readString(info.lastName)]
        .filter(Boolean)
        .join(" ")
        .trim();
    })
    .filter(Boolean);
  const childName = childNames[0] ?? "";
  const childNameList = [...new Set(childNames)];

  const id = readString(value.id);
  const childGradeLabels = childRecords
    .map((child) =>
      normalizeGradeLabel(
        readString(child.gradeApplyingFor) ||
          readString(asRecord(child.childInfo).currentGrade),
      ),
    )
    .filter(Boolean);
  const grade =
    childGradeLabels.length > 0
      ? [...new Set(childGradeLabels)].join(", ")
      : normalizeGradeLabel(
          readString(value.gradeApplyingFor) || readString(value.grade),
        );
  const submittedAtEpochSeconds = asUnixSeconds(
    value.submittedAt ?? value.createdAt ?? value.updatedAt,
  );
  const stageReferenceEpochSeconds = resolveStageReferenceEpochSeconds(value);
  const interviewDateEpochSeconds = asUnixSeconds(value.interviewDate);
  const status = toApplicantStatus(value.status);
  const studentsCount = children.length || Number(value.studentCount ?? 0);
  const assigneeName = resolveAssigneeLabel(
    value.assigneeName,
    asRecord(value.assignee).name,
    firstChild.assigneeName,
    asRecord(firstChild.assignee).name,
    firstChild.assignedTo,
    value.assignedTo,
  );
  const familyName = readString(applicant.familyName);

  return {
    id,
    name:
      childNameList.length > 1
        ? childNameList.join(", ")
        : childName ||
          contactName ||
          familyName ||
          readString(value.code) ||
          id,
    grade,
    division: undefined,
    status,
    statusLabel: undefined,
    avatar:
      readString(childInfo.avatarUrl) ||
      readString(primaryContact.avatarUrl) ||
      "",
    date: formatShortDateFromUnixSeconds(submittedAtEpochSeconds),
    progress: undefined,
    interviewDate: formatShortDateTimeFromUnixSeconds(
      interviewDateEpochSeconds,
    ),
    interviewRoom: readString(value.interviewRoom) || undefined,
    priority: Boolean(value.priority),
    studentsCount: Number.isFinite(studentsCount) ? studentsCount : undefined,
    assigneeName: assigneeName || undefined,
    assigneeAvatar: readString(asRecord(value.assignee).avatarUrl) || undefined,
    siblings: Array.isArray(value.siblings)
      ? (value.siblings as string[]).map((s) => readString(s)).filter(Boolean)
      : undefined,
    applicationCode: readString(value.code) || null,
    familyName: familyName || null,
    submittedAtEpochSeconds,
    submittedAtIso: toIsoFromUnixSeconds(submittedAtEpochSeconds),
    stageReferenceEpochSeconds,
    interviewDateEpochSeconds,
    interviewDateIso: toIsoFromUnixSeconds(interviewDateEpochSeconds),
    priorityCount: Boolean(value.priority) ? 1 : 0,
    financialAidEnabled: isFinancialAidAccessEnabledForApplication(value),
  };
};

export const buildAdmissionsDashboardArtifactDataset = (input: {
  items: Record<string, unknown>[];
  totalCount: number;
  windowLimit?: number;
}): AdmissionsDashboardArtifactDataset => {
  const applicants = input.items.map((item) =>
    toAdmissionsDashboardApplicant(item),
  );
  const status = buildSummaryRows(
    applicants,
    (applicant) => applicant.status,
    (left, right) => {
      const leftIndex = ADMISSIONS_DASHBOARD_STATUS_ORDER.indexOf(
        left.label as Applicant["status"],
      );
      const rightIndex = ADMISSIONS_DASHBOARD_STATUS_ORDER.indexOf(
        right.label as Applicant["status"],
      );
      if (leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
      }
      return left.label.localeCompare(right.label);
    },
  );
  const grade = buildSummaryRows(
    applicants,
    (applicant) => applicant.grade,
    (left, right) => sortGradeLabel(left.label, right.label),
  );
  const assignee = buildSummaryRows(
    applicants,
    (applicant) => applicant.assigneeName || "Unassigned",
    (left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }
      return left.label.localeCompare(right.label);
    },
  );
  const submissionMonth = buildSummaryRows(
    applicants,
    (applicant) => toMonthBucket(applicant.submittedAtEpochSeconds),
    (left, right) => {
      if (left.label === "Unknown month") return 1;
      if (right.label === "Unknown month") return -1;
      return (
        new Date(`1 ${left.label}`).getTime() -
        new Date(`1 ${right.label}`).getTime()
      );
    },
  );

  return {
    sourceId: "admissions_dashboard_live",
    route: ADMISSIONS_DASHBOARD_ARTIFACT_ROUTE,
    generatedAt: new Date().toISOString(),
    windowLimit: input.windowLimit ?? ADMISSIONS_DASHBOARD_ARTIFACT_LIMIT,
    totalCount: input.totalCount,
    applicantCount: applicants.length,
    applicants,
    summaries: {
      status,
      grade,
      assignee,
      submissionMonth,
    },
  };
};

const resolveChartKind = (
  normalizedQuestion: string,
): AdmissionsDashboardChartKind => {
  if (/\bstacked\b/.test(normalizedQuestion)) {
    return "stacked-bar";
  }
  if (/\barea\b/.test(normalizedQuestion)) {
    return "area";
  }
  if (/\bline\b|\btrend line\b/.test(normalizedQuestion)) {
    return "line";
  }
  return "bar";
};

const resolveChartDimension = (
  normalizedQuestion: string,
): AdmissionsDashboardChartDimension | null => {
  if (/\bgrade\b/.test(normalizedQuestion)) {
    return "grade";
  }
  if (
    /\bassignee\b|\bstaff\b|\bowner\b|\bcounselor\b|\badmissions rep\b/.test(
      normalizedQuestion,
    )
  ) {
    return "assignee";
  }
  if (
    /\bover time\b|\btrend\b|\bmonthly\b|\bmonth\b|\bsubmitted\b|\bsubmission date\b|\bsubmission\b/.test(
      normalizedQuestion,
    )
  ) {
    return "submission_month";
  }
  if (
    /\bstatus\b|\bpipeline\b|\bstage\b|\baccepted\b|\bapplied\b|\bwaitlisted\b|\benrolled\b|\bdenied\b|\bscheduled\b|\bin-progress\b/.test(
      normalizedQuestion,
    )
  ) {
    return "status";
  }
  return null;
};

export const resolveAdmissionsDashboardChartRequest = (
  question: string,
): AdmissionsDashboardChartRequest | null => {
  const normalizedQuestion = question.toLowerCase();
  if (UNSUPPORTED_DASHBOARD_CHART_PATTERN.test(normalizedQuestion)) {
    return null;
  }
  const defaultToStatusPageData =
    PAGE_SCOPED_CHART_PATTERN.test(normalizedQuestion);
  const dimension =
    resolveChartDimension(normalizedQuestion) ??
    (defaultToStatusPageData ? "status" : null);
  if (!dimension) {
    return null;
  }
  const kind = resolveChartKind(normalizedQuestion);
  if (
    (kind === "line" || kind === "area") &&
    dimension !== "submission_month"
  ) {
    return null;
  }

  const dimensionLabel = {
    status: "Applications by status",
    grade: "Applications by grade",
    assignee: "Applications by assignee",
    submission_month: "Applications by submission month",
  }[dimension];

  const request = {
    kind,
    dimension,
    title:
      defaultToStatusPageData && dimension === "status"
        ? "Admissions dashboard chart"
        : dimensionLabel,
    subtitle:
      defaultToStatusPageData && dimension === "status"
        ? "Generated directly from the current admissions dashboard page data."
        : kind === "stacked-bar"
          ? "Stacked by priority vs standard applications."
          : "Generated directly from the live admissions dashboard data window.",
  };
  return defaultToStatusPageData && dimension === "status"
    ? { ...request, preset: "status_page_data" }
    : request;
};

export const resolveAdmissionsDashboardArtifactSupport = (input: {
  pathname: string;
  workflowKey: string;
  question: string;
}): AdmissionsDashboardArtifactSupport => {
  if (!isAdmissionsDashboardArtifactRoute(input.pathname)) {
    return {
      supported: false,
      fallbackToChat: true,
      reason:
        "Direct artifact generation is currently supported only on the live admissions dashboard route aliases.",
    };
  }

  if (input.workflowKey === "artifact.spreadsheet.generate") {
    return {
      supported: true,
      fallbackToChat: false,
      sourceId: "admissions_dashboard_live",
    };
  }

  if (input.workflowKey === "artifact.visualization3d.generate") {
    return {
      supported: true,
      fallbackToChat: false,
      sourceId: "admissions_dashboard_live",
    };
  }

  if (input.workflowKey === "artifact.chart.generate") {
    const chartRequest = resolveAdmissionsDashboardChartRequest(input.question);
    if (!chartRequest) {
      return {
        supported: false,
        fallbackToChat: true,
        reason:
          "Supported dashboard charts are limited to status, grade, assignee, or submission-month views.",
      };
    }
    return {
      supported: true,
      fallbackToChat: false,
      sourceId: "admissions_dashboard_live",
      chartRequest,
    };
  }

  return {
    supported: false,
    fallbackToChat: false,
    reason:
      "Workflow is not handled by the admissions dashboard direct artifact lane.",
  };
};
